/**
 * Resolves the header / breadcrumb title for a pathname.
 * Order matters: more specific routes must come before their parents.
 */
const INVOICE_PROFILE_CONFIGURATION_BASE = "Invoice Profile Configuration";

/** Full page/breadcrumb title for invoice profile; omits suffix when name is empty or placeholder. */
export function formatInvoiceProfileConfigurationTitle(clientName) {
  const name = (clientName ?? "").trim();
  if (!name || name === "—") {
    return INVOICE_PROFILE_CONFIGURATION_BASE;
  }
  return `${INVOICE_PROFILE_CONFIGURATION_BASE} - ${name}`;
}

/** Last client context for Invoice Profile (tab refresh, return navigation). */
export const INVOICE_PROFILE_CLIENT_SESSION_KEY = "hibbert.invoiceProfileClientContext";

/** @returns {{ clientCode: string; clientName: string; custId?: number } | null} */
export function readInvoiceProfileClientFromSession() {
  try {
    const raw = sessionStorage.getItem(INVOICE_PROFILE_CLIENT_SESSION_KEY);
    if (!raw) {
      return null;
    }
    const o = JSON.parse(raw);
    if (!o || typeof o !== "object") {
      return null;
    }
    const clientCode = String(o.clientCode ?? "").trim();
    const clientName = String(o.clientName ?? "").trim();
    const custIdRaw = o.custId;
    const custIdNum = Number.parseInt(String(custIdRaw ?? ""), 10);
    const custId = Number.isFinite(custIdNum) && custIdNum > 0 ? custIdNum : undefined;
    if (!clientCode && !clientName && custId == null) {
      return null;
    }
    return { clientCode, clientName, custId };
  } catch {
    return null;
  }
}

export function writeInvoiceProfileClientToSession({
  clientCode = "",
  clientName = "",
  custId,
} = {}) {
  try {
    const code = String(clientCode ?? "").trim();
    const name = String(clientName ?? "").trim();
    const cid = custId != null ? Number.parseInt(String(custId), 10) : NaN;
    const custIdOut = Number.isFinite(cid) && cid > 0 ? cid : undefined;
    if (!code && !name && custIdOut == null) {
      sessionStorage.removeItem(INVOICE_PROFILE_CLIENT_SESSION_KEY);
      return;
    }
    sessionStorage.setItem(
      INVOICE_PROFILE_CLIENT_SESSION_KEY,
      JSON.stringify({ clientCode: code, clientName: name, custId: custIdOut })
    );
  } catch {
    /* ignore */
  }
}

/** Aligns with BillingProfileConfiguration client display (URL + session + localStorage + defaults). */
function resolveClientNameForBillingProfileTitle(searchStr) {
  const params = new URLSearchParams(searchStr);
  const nameFromUrl = (params.get("clientName") ?? "").trim();
  if (nameFromUrl) {
    return nameFromUrl;
  }
  const persisted = readInvoiceProfileClientFromSession();
  const urlCustId = Number.parseInt(params.get("custId") ?? "", 10);
  const hasCustIdContext =
    (Number.isFinite(urlCustId) && urlCustId > 0) ||
    (persisted?.custId != null && Number(persisted.custId) > 0);
  const nameHint = (persisted?.clientName ?? "").trim();
  if (hasCustIdContext && nameHint) {
    return nameHint;
  }
  const codeFromUrl = (params.get("clientCode") ?? "").trim() || (persisted?.clientCode ?? "").trim();
  const defaultClients = [
    { id: 1, name: "Acme Corporation", code: "ACME" },
    { id: 2, name: "Global Tech Inc", code: "GLBL" },
    { id: 3, name: "Stellar Industries", code: "STLR" },
  ];
  try {
    const raw = localStorage.getItem("clients");
    let parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) {
      parsed = [];
    }
    const existingIds = new Set(parsed.map((c) => c.id));
    const allClients = [...parsed, ...defaultClients.filter((c) => !existingIds.has(c.id))];
    if (codeFromUrl) {
      const byCode = allClients.find(
        (c) => String(c.code ?? "").toUpperCase() === codeFromUrl.toUpperCase()
      );
      if (byCode?.name?.trim()) {
        return byCode.name.trim();
      }
    }
    if (nameHint) {
      const lowered = nameHint.toLowerCase();
      const byName = allClients.find(
        (c) => (c.name || "").trim().toLowerCase() === lowered
      );
      if (byName?.name?.trim()) {
        return byName.name.trim();
      }
    }
    if (allClients.length > 0) {
      return (allClients[0].name || allClients[0].code || "").trim();
    }
  } catch {
    /* ignore */
  }
  return "";
}

export function getPageTitle(pathname, search = "") {
  const path =
    pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";

  const searchStr =
    typeof search === "string"
      ? search.startsWith("?")
        ? search.slice(1)
        : search
      : "";
  const rules = [
    [/^\/?$/, "Single Sign-On"],
    [/^\/ssologin\.action$/, "Single Sign-On"],
    [/^\/sso-error$/, "Single Sign-On Error"],
    [/^\/dashboard$/, "Dashboard"],
    [/^\/login$/, "Sign In"],
    [/^\/clients\/[^/]+\/po-jackets$/, "PO and Jacket Management"],
    [/^\/clients\/[^/]+$/, "Client Configuration"],
    [/^\/clients$/, "Clients"],
    [/^\/billing-profile$/, INVOICE_PROFILE_CONFIGURATION_BASE],
    [/^\/contracts-billing$/, "Contracts & Billing Setup"],
    [/^\/activity-entry$/, "Monthly Activity Entry"],
    [/^\/invoices\/new\/review$/, "Generate Invoice"],
    [/^\/invoices\/[^/]+\/review$/, "Draft Invoice Review"],
    [/^\/invoices\/[^/]+\/view$/, "Invoice Details"],
    [/^\/invoices$/, "Invoices"],
    [/^\/accounting-allocation\/rules$/, "Accounting Allocation Rules"],
    [/^\/accounting-allocation\/detail$/, "Invoice Allocation Detail"],
    [/^\/accounting-allocation$/, "Accounting Allocation"],
    [/^\/reports$/, "Reports"],
    [/^\/service-catalog$/, "Offer Catalog"],
    [/^\/user-activity$/, "Recent User Activity"],
    [/^\/roles\/create$/, "Create New Role"],
    [/^\/role-privileges\/create$/, "Create New Privilege"],
    [/^\/role-privileges$/, "Assigned Role Privileges"],
    [/^\/admin\/server-logs$/, "Server Logs"],
    [/^\/admin\/automated-invoice-job$/, "Automated Invoice Create Job"],
    [/^\/cron\/monthly-invoice$/, "Cron (monthly invoice job)"],
    [/^\/cron\/user-activity-purge$/, "Cron (user-activity purge)"],
    [/^\/admin/, "Administration"],
  ];

  for (const [regex, title] of rules) {
    if (regex.test(path)) {
      if (path === "/billing-profile") {
        return formatInvoiceProfileConfigurationTitle(
          resolveClientNameForBillingProfileTitle(searchStr)
        );
      }
      if (isClientConfigurationPath(path)) {
        const tab = new URLSearchParams(searchStr).get("tab");
        if (tab) {
          const tabLabel = getClientConfigurationTabLabel(tab);
          if (tabLabel && tabLabel !== "Client Info") {
            return `${title} — ${tabLabel}`;
          }
        }
      }
      return title;
    }
  }

  return "Hibbert Accounting";
}

const CLIENT_CONFIG_TAB_LABELS = {
  "client-info": "Client Info",
  jackets: "Jackets",
  contracts: "Contracts",
  "billing-profiles": "Invoice Profile Configuration",
  invoices: "Invoices",
  "invoice-config": "Invoice Configuration",
};

/** Matches `/clients/:id` but not `/clients/:id/po-jackets`. */
export function isClientConfigurationPath(pathname) {
  const path = pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";
  return /^\/clients\/[^/]+$/.test(path);
}

/** Tab label for Client Configuration header breadcrumb; defaults to Client Info. */
export function getClientConfigurationTabLabel(tabParam) {
  if (!tabParam) return CLIENT_CONFIG_TAB_LABELS["client-info"];
  return CLIENT_CONFIG_TAB_LABELS[tabParam] ?? CLIENT_CONFIG_TAB_LABELS["client-info"];
}
